import { yupResolver } from "@hookform/resolvers/yup";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  Card,
  Divider,
  Page,
  TextField,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import React, { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { Colors } from "react-native-ui-lib";
import { CountProductModalRootParamList } from "../../../../types";
import Product from "../../../../utilities/data/model/Product";
import {
  StockAdjusterFormValues,
  stockAdjusterValidationSchema,
} from "../../../../utilities/stock-count";
import { useCountProductModalRootContext } from "../../../../utilities/stock-count/hooks";
import { CountItemFormValues } from "../CountNonProductModalScreen/types";
import { useStockCounter } from "../StockCountScreen/hooks";
import { ProductSummaryCard } from "../../../products/components";

export type CountProductModalScreenProps = StackScreenProps<
  CountProductModalRootParamList,
  "CountProductModalScreen"
>;

const CountProduct = ({
  product,
  navigation,
}: CountProductModalScreenProps & {
  product: Product;
}) => {
  const { stockCount } = useCountProductModalRootContext();

  const { countStockItem } = useStockCounter();

  const {
    handleSubmit,
    formState: {
      isDirty,
      isLoading,
      isSubmitting,
      isValid,
      isSubmitSuccessful,
    },
  } = useFormContext<CountItemFormValues>();

  const { showMessage } = useFlashMessages();

  const handleOnSubmit = React.useCallback(
    async (fieldValues: CountItemFormValues) => {
      if (!stockCount.id) return;
      countStockItem(
        stockCount.id,
        { barcode: product.stockNumber },
        fieldValues.counted
      )
        .then(() => {
          navigation.getParent()?.goBack();
        })
        .catch((e) => {
          console.log(e);
          showMessage({
            severity: "error",
            message: "Failed to add non-product",
          });
        });
    },
    [navigation, stockCount.id, countStockItem, showMessage]
  );

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label="Add New Product"
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page footer={footerMarkup}>
      {product.id && (
        <ProductSummaryCard
          productId={product.id}
          locationId={stockCount.location.id}
        />
      )}
      <Divider />
      <Card title="Count Details">
        <Controller
          name="counted"
          render={({ field, fieldState, formState }) => {
            return (
              <TextField
                label="Amount"
                inputMode={"numeric" as const}
                keyboardType={"numeric" as const}
                placeholder={"Amount"}
                onChangeText={field.onChange}
                validationMessage={fieldState.error?.message?.toString()}
              />
            );
          }}
        />
      </Card>
    </Page>
  );
};

export const CountProductModalScreen = (
  props: CountProductModalScreenProps
) => {
  const database = useDatabase();
  const [product, setProduct] = useState<Product | undefined>(undefined);

  const { route } = props;
  const { productId } = route.params;

  useEffect(() => {
    if (!productId) {
      return setProduct(undefined);
    }
    fetchProduct(productId)
      .then(setProduct)
      .catch(() => {
        setProduct(undefined);
      });
  }, [productId]);

  async function fetchProduct(id: string) {
    return database.collections.get<Product>("products").find(id);
  }

  const methods = useForm<StockAdjusterFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      counted: 1,
    },
    resolver: yupResolver(stockAdjusterValidationSchema),
  });

  if (!product) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <CountProduct product={product} {...props} />
    </FormProvider>
  );
};
